import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { http, errorText } from "@/lib/api";

const usd = (v) => `$${Number(v || 0).toFixed(2)}`;
const REASON = {
  ok: null,
  over_limit_warn: "The main model is over its monthly limit. Ceres keeps answering (warn-only mode).",
  switched_over_limit: "The main model reached its monthly limit, so Ceres switched to the next available model.",
  paused_over_limit: "Both models are unavailable or over their limits. Ceres is paused and visitors see a 'try again later' message.",
  paused_off: "No model is available (off or missing its key). Ceres is paused for visitors.",
};

export const AiAlert = ({ ai }) => {
  if (!ai) return null;
  const near = ai.models.filter((m) => m.near_limit || m.limit_reached);
  const msg = REASON[ai.reason];
  if (!msg && near.length === 0) return null;
  return (
    <div role="alert" className="flex items-start gap-3 border border-[#f26b21] bg-[#fff4ec] p-4 text-[13px] text-hv-text" data-testid="ai-limit-alert">
      <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[#f26b21]" aria-hidden="true" />
      <div>
        {msg && <p className="font-semibold">{msg}</p>}
        {near.map((m) => <p key={m.key}>{m.label}: {m.limit_reached ? "monthly limit reached" : "over 80% of its monthly limit"}.</p>)}
      </div>
    </div>
  );
};

const SpendBar = ({ m }) => (
  <div data-testid={`ai-spend-${m.key}`}>
    <div className="flex justify-between text-[12px]"><span className="text-hv-muted">This month</span><span className="font-semibold tabular-nums">{usd(m.spend_usd)} of {usd(m.monthly_limit_usd)} ({m.used_pct}%)</span></div>
    <div className="mt-1 h-2 bg-hv-section"><div className={`h-full ${m.limit_reached ? "bg-red-600" : m.near_limit ? "bg-[#f26b21]" : "bg-hv-logo-navy"}`} style={{ width: `${Math.min(100, m.used_pct)}%` }} /></div>
  </div>
);

const NumField = ({ label, value, onChange, testid, step = "0.01" }) => (
  <label className="block text-[11px] font-semibold uppercase tracking-wider text-hv-muted">
    {label}
    <input type="number" min="0" step={step} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="mt-1 w-full border border-hv-rule px-2 py-1.5 text-[13px] normal-case tracking-normal text-hv-text focus:border-hv-navy focus:outline-none" />
  </label>
);

const ModelCard = ({ m, draft, isSuper, canSwitch, isActive, onChange, onActive }) => (
  <div className={`border p-4 ${isActive ? "border-hv-navy" : "border-hv-rule"}`} data-testid={`ai-model-card-${m.key}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="font-heading text-[14px] font-bold text-hv-navy">{m.label}</p>
        <p className="text-[12px] text-hv-muted">{m.via}</p>
        {!m.key_ready && <p className="mt-1 text-[12px] font-semibold text-red-700" data-testid={`ai-key-missing-${m.key}`}>API key not added yet</p>}
      </div>
      {canSwitch ? (
        <Switch checked={draft.enabled} onCheckedChange={(v) => onChange({ enabled: v })} aria-label={`${m.label} on or off`} data-testid={`ai-model-toggle-${m.key}`} />
      ) : (
        <span className="text-[12px] font-semibold" data-testid={`ai-model-state-${m.key}`}>{m.enabled ? "On" : "Off"}</span>
      )}
    </div>
    {canSwitch && (
      <label className="mt-3 flex items-center gap-2 text-[13px]">
        <input type="radio" name="ai-active" checked={isActive} onChange={onActive} data-testid={`ai-active-radio-${m.key}`} /> Main model
      </label>
    )}
    <p className="mt-2 text-[12px] text-hv-muted">{m.calls.toLocaleString("en-CA")} AI calls this month</p>
    {isSuper && (
      <div className="mt-3 space-y-3">
        <SpendBar m={m} />
        <div className="grid grid-cols-3 gap-2">
          <NumField label="Monthly limit US$" step="1" value={draft.monthly_limit_usd} onChange={(v) => onChange({ monthly_limit_usd: v })} testid={`ai-limit-input-${m.key}`} />
          <NumField label="$ / 1M input" value={draft.price_in} onChange={(v) => onChange({ price_in: v })} testid={`ai-price-in-input-${m.key}`} />
          <NumField label="$ / 1M output" value={draft.price_out} onChange={(v) => onChange({ price_out: v })} testid={`ai-price-out-input-${m.key}`} />
        </div>
      </div>
    )}
  </div>
);

const toDraft = (ai) => ({
  active: ai.active, on_limit: ai.on_limit,
  models: Object.fromEntries(ai.models.map((m) => [m.key, { enabled: m.enabled, monthly_limit_usd: m.monthly_limit_usd, price_in: m.price_in, price_out: m.price_out }])),
});

export const AiControlPanel = ({ ai, isSuper, onSaved }) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (ai) setDraft(toDraft(ai)); }, [ai]);
  if (!ai) return null;
  const setModel = (k, patch) => setDraft((d) => ({ ...d, models: { ...d.models, [k]: { ...d.models[k], ...patch } } }));
  const save = async () => {
    setSaving(true);
    try {
      const models = Object.fromEntries(Object.entries(draft.models).map(([k, v]) => [k, { enabled: v.enabled, monthly_limit_usd: Number(v.monthly_limit_usd), price_in: Number(v.price_in), price_out: Number(v.price_out) }]));
      const { data } = isSuper
        ? await http.put("/admin/ai", { ...draft, models })
        : await http.put("/admin/ai/switch", { active: draft.active, models: Object.fromEntries(Object.entries(draft.models).map(([k, v]) => [k, { enabled: v.enabled }])) });
      onSaved(data);
      toast("AI settings saved");
    } catch (e) {
      toast.error(errorText(e.response?.data?.detail));
      setDraft(toDraft(ai));
    } finally { setSaving(false); }
  };
  return (
    <section className="border border-hv-rule bg-white p-6" data-testid="panel-ai-models">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hv-rule pb-3">
        <h2 className="font-heading text-[14px] font-semibold text-hv-text">AI models <span className="font-normal text-hv-muted">· one model answers at a time · limits reset on the 1st of each month</span></h2>
        <span className="text-[12px] font-semibold text-hv-navy" data-testid="ai-answering-now">Answering now: {ai.answering ? ai.models.find((m) => m.key === ai.answering).label : "Paused"}</span>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {ai.models.map((m) => (
          <ModelCard key={m.key} m={m} isSuper={isSuper} canSwitch={Boolean(draft)} draft={draft?.models[m.key] || m} isActive={(draft?.active || ai.active) === m.key}
            onChange={(p) => setModel(m.key, p)} onActive={() => setDraft((d) => ({ ...d, active: m.key }))} />
        ))}
      </div>
      {draft && (
        <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
          {isSuper ? <fieldset className="text-[13px]" data-testid="ai-on-limit-group">
            <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-hv-muted">When the main model reaches its limit</legend>
            <label className="mr-5"><input type="radio" name="on-limit" checked={draft.on_limit === "switch"} onChange={() => setDraft({ ...draft, on_limit: "switch" })} data-testid="ai-on-limit-switch" /> Switch to the next available model, then pause</label>
            <label><input type="radio" name="on-limit" checked={draft.on_limit === "warn"} onChange={() => setDraft({ ...draft, on_limit: "warn" })} data-testid="ai-on-limit-warn" /> Keep answering, just warn admins</label>
          </fieldset> : <span />}
          <button type="button" onClick={save} disabled={saving} className="hv-btn-light hv-btn-sm" data-testid="ai-settings-save-button">{saving ? "Saving…" : "Save AI settings"}</button>
        </div>
      )}
      {isSuper && <p className="mt-3 text-[11px] text-hv-muted">Spend is estimated from tokens used × the prices above. Your provider's invoice is final.</p>}
    </section>
  );
};
